import * as React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ApiError, Badge, Button, Spinner, cn } from "@toss-ads/ui";
import { getAdvertiser } from "../lib/api";
import { AdvertiserLeadsSection } from "../components/AdvertiserLeadsSection";
import { AdvertiserStatsSection } from "../components/AdvertiserStatsSection";
import { useUrlState } from "../lib/use-url-state";

// 광고주 상세 — 뷰 전용(잠재고객/통계/부재 탭). 이름·slug·활성·secret 수정은
// 광고주 목록의 "수정" 모달에서.
export function AdvertiserEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const {
    data: existing,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["admin", "advertisers", id],
    queryFn: () => getAdvertiser(id!),
    enabled: !!id,
  });

  const [tab, setTab] = useUrlState("tab", "leads");

  if (isLoading) {
    return (
      <div>
        <BackBar onClick={() => navigate("/advertisers")} />
        <div className="rounded-lg border bg-surface py-16 flex justify-center text-ink-subtle">
          <Spinner size={20} />
        </div>
      </div>
    );
  }

  if (isError || !existing) {
    return (
      <div>
        <BackBar onClick={() => navigate("/advertisers")} />
        <div className="rounded-lg border bg-surface py-12 text-center text-[13px] text-danger">
          {error instanceof ApiError
            ? error.message
            : "광고주를 불러오지 못했습니다."}
        </div>
      </div>
    );
  }

  return (
    <div>
      <BackBar onClick={() => navigate("/advertisers")} />

      <div className="mb-4 min-w-0">
        <h1 className="text-[18px] font-semibold tracking-tight truncate">
          {existing.name}
        </h1>
        <div className="mt-2">
          <Badge variant={existing.active ? "success" : "neutral"} dot>
            {existing.active ? "활성" : "비활성"}
          </Badge>
        </div>
      </div>

      <div className="flex items-center gap-1 border-b border-border mb-3">
        {(["leads", "stats", "absent"] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={cn(
              "px-3 py-2 text-[13px] border-b-2 -mb-px transition-colors",
              tab === t
                ? "border-primary text-primary font-medium"
                : "border-transparent text-ink-muted hover:text-ink",
            )}
          >
            {t === "leads" ? "잠재고객" : t === "stats" ? "통계" : "부재"}
          </button>
        ))}
      </div>

      {tab === "leads" ? (
        <AdvertiserLeadsSection advertiserId={existing.id} />
      ) : tab === "stats" ? (
        <AdvertiserStatsSection advertiserId={existing.id} />
      ) : (
        <AdvertiserLeadsSection advertiserId={existing.id} variant="absent" />
      )}
    </div>
  );
}

function BackBar({ onClick }: { onClick: () => void }) {
  return (
    <div className="flex items-center gap-2 mb-4">
      <Button variant="text" size="md" onClick={onClick}>
        ← 광고주 목록
      </Button>
    </div>
  );
}
